import { OrderHistory, EmailProductData } from '../stores/appOrderHistoryStore';
import { isFoodProduct } from '../components/orderhistory/utils';
import { getOrderTotal, getProductPrice } from '../components/orderhistory/vatUtils';
import { platformsWithPerKgPrice } from './platformUtils';
import { startOfPeriod, normalizeDate, addPeriod } from './dateUtils';
import { TimeViewOption } from '../components/ui/TimeControls';
import { getWeek, getYear } from 'date-fns';
/**
 * Calculate total spent from order history data
 */
export function calculateTotalSpent(
  orderData: OrderHistory[],
  showExVat: boolean
): number {
  return orderData.reduce((sum, o) => sum + getOrderTotal(o, showExVat), 0);
}

/**
 * Average spend per period (day/week/month) across the supplied order history.
 * Mirrors `calculateAverageRevenue`: groups orders into periods over the full history
 * (excluding the current, incomplete period) and averages the non-zero per-period totals.
 */
export function calculateAverageSpent(
  orderData: OrderHistory[],
  showExVat: boolean,
  selectedTimeOption: TimeViewOption
): number {
  switch (selectedTimeOption) {
    case 'day':
      return getDailySpentAverage(orderData, showExVat);
    case 'week':
      return getWeeklySpentAverage(orderData, showExVat);
    case 'month':
      return getMonthlySpentAverage(orderData, showExVat);
    case 'all':
    default:
      return 0;
  }
}

function getDailySpentAverage(orderData: OrderHistory[], showExVat: boolean): number {
  if (orderData.length === 0) return 0;
  const todayKey = new Date().toISOString().split('T')[0];
  const dayMap = new Map<string, number>();
  orderData.forEach(order => {
    const dateKey = new Date(order.orderDate).toISOString().split('T')[0];
    if (dateKey === todayKey) return;
    dayMap.set(dateKey, (dayMap.get(dateKey) || 0) + getOrderTotal(order, showExVat));
  });
  const nonZero = Array.from(dayMap.values()).filter(v => v > 0);
  if (nonZero.length === 0) return 0;
  return nonZero.reduce((s, v) => s + v, 0) / nonZero.length;
}

function getWeeklySpentAverage(orderData: OrderHistory[], showExVat: boolean): number {
  if (orderData.length === 0) return 0;
  const today = normalizeDate(new Date());
  const currentWeekKey = getYear(today) * 100 + getWeek(today);
  const weekMap = new Map<number, number>();
  orderData.forEach(order => {
    const orderDate = normalizeDate(new Date(order.orderDate));
    const weekKey = getYear(orderDate) * 100 + getWeek(orderDate);
    if (weekKey === currentWeekKey) return;
    weekMap.set(weekKey, (weekMap.get(weekKey) || 0) + getOrderTotal(order, showExVat));
  });
  const nonZero = Array.from(weekMap.values()).filter(v => v > 0);
  if (nonZero.length === 0) return 0;
  return nonZero.reduce((s, v) => s + v, 0) / nonZero.length;
}

function getMonthlySpentAverage(orderData: OrderHistory[], showExVat: boolean): number {
  if (orderData.length === 0) return 0;
  const today = new Date();
  const currentMonthKey = `${today.getFullYear()}-${today.getMonth()}`;
  const monthMap = new Map<string, number>();
  orderData.forEach(order => {
    const orderDate = normalizeDate(new Date(order.orderDate));
    const monthKey = `${getYear(orderDate)}-${orderDate.getMonth()}`;
    if (monthKey === currentMonthKey) return;
    monthMap.set(monthKey, (monthMap.get(monthKey) || 0) + getOrderTotal(order, showExVat));
  });
  const nonZero = Array.from(monthMap.values()).filter(v => v > 0);
  if (nonZero.length === 0) return 0;
  return nonZero.reduce((s, v) => s + v, 0) / nonZero.length;
}

/**
 * Calculate total food-only spending from order history data
 */
export function calculateTotalFoodSpent(
  orderData: OrderHistory[],
  showExVat: boolean
): number {
  return orderData.reduce((sum, order) => {
    const foodCost = (order.productData || []).reduce((foodSum: number, product: EmailProductData) => {
      if (isFoodProduct(product)) {
        const price = getProductPrice(product, showExVat);
        const qty = platformsWithPerKgPrice.includes(order.platform.toLowerCase())
          ? product.rawQuantity
          : product.quantity;
        return foodSum + (price * qty);
      }
      return foodSum;
    }, 0);
    return sum + foodCost;
  }, 0);
}

/**
 * Calculate supplier spending breakdown
 */
export interface SupplierSpending {
  supplier: string;
  value: number;
  unknownSupplierName?: string;
}

/**
 * Get the supplier key for grouping orders
 * For any_invoice platform with unknownSupplierName, use the unknownSupplierName
 * Otherwise use the platform name
 */
function getSupplierKey(order: OrderHistory): string {
  const platform = order.platform.toLowerCase();
  if (platform === 'any_invoice' && order.unknownSupplierName) {
    return order.unknownSupplierName;
  }
  return platform;
}

export function calculateFoodOnlySupplierSpending(
  orderData: OrderHistory[],
  showExVat: boolean
): SupplierSpending[] {
  const totals: Record<string, number> = {};

  orderData.forEach(order => {
    const supplierKey = getSupplierKey(order);
    // Calculate food-only spending for this order
    const foodCost = (order.productData || []).reduce((foodSum: number, product: EmailProductData) => {
      if (isFoodProduct(product)) {
        const price = getProductPrice(product, showExVat);
        const qty = platformsWithPerKgPrice.includes(order.platform.toLowerCase())
          ? product.rawQuantity
          : product.quantity;
        return foodSum + (price * qty);
      }
      return foodSum;
    }, 0);
    totals[supplierKey] = (totals[supplierKey] || 0) + foodCost;
  });

  return Object.entries(totals)
    .map(([supplier, value]) => ({ supplier, value }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Calculate full supplier spending breakdown (including non-food items)
 * For any_invoice orders, splits by unknownSupplierName instead of grouping under 'any_invoice'
 */
export function calculateFullSupplierSpending(
  orderData: OrderHistory[],
  showExVat: boolean
): SupplierSpending[] {
  const totals: Record<string, number> = {};

  orderData.forEach(order => {
    const supplierKey = getSupplierKey(order);
    const orderTotal = getOrderTotal(order, showExVat);
    totals[supplierKey] = (totals[supplierKey] || 0) + orderTotal;
  });

  return Object.entries(totals)
    .map(([supplier, value]) => ({ supplier, value }))
    .sort((a, b) => b.value - a.value);
}

export interface GraphDataPoint {
  label: string;
  value: number;
  periodStart: Date;
}

/**
 * Calculate graph data points for costs over time.
 * Returns up to 12 data points (never going into the future) with 0 for missing data.
 *
 * @param orderData - All order history data
 * @param selectedPeriodStart - The start date of the currently selected period
 * @param view - The view type (day/week/month/all)
 * @param showExVat - Whether to show excluding VAT
 * @returns Array of graph data points with labels and values
 */
export function calculateCostGraphData(
  orderData: OrderHistory[],
  selectedPeriodStart: Date,
  selectedPeriodEnd: Date,
  view: 'day' | 'week' | 'month' | 'quarter' | 'all',
  showExVat: boolean
): GraphDataPoint[] {
  // For 'all' view, use daily data points for the exact custom date range
  if (view === 'all') {
    return calculateCustomRangeGraphData(orderData, selectedPeriodStart, selectedPeriodEnd, showExVat);
  }

  // Group orders by period
  const periodMap = new Map<string, number>();

  orderData.forEach(order => {
    const orderDate = normalizeDate(new Date(order.orderDate));
    const periodStart = startOfPeriod(orderDate, view);
    const periodKey = periodStart.toISOString();
    periodMap.set(periodKey, (periodMap.get(periodKey) || 0) + getOrderTotal(order, showExVat));
  });

  // Get today and normalize it
  const today = normalizeDate(new Date());
  const todayPeriodStart = startOfPeriod(today, view);

  // Calculate 12 periods ending at the selected period (or today if selected is in the future)
  const maxPoints = 12;
  const selectedPeriodNormalized = startOfPeriod(selectedPeriodStart, view);

  // End at the selected period, but don't go beyond today
  const endPeriod = selectedPeriodNormalized > todayPeriodStart ? todayPeriodStart : selectedPeriodNormalized;

  // Start 11 periods before the end period
  let currentPeriod = new Date(endPeriod);
  for (let i = 0; i < maxPoints - 1; i++) {
    currentPeriod = addPeriod(currentPeriod, view, -1);
  }

  // Generate all 12 periods with data or 0
  const periods: GraphDataPoint[] = [];
  for (let i = 0; i < maxPoints; i++) {
    const periodKey = currentPeriod.toISOString();
    periods.push({
      label: formatPeriodLabel(currentPeriod, view),
      value: periodMap.get(periodKey) || 0,
      periodStart: new Date(currentPeriod)
    });
    currentPeriod = addPeriod(currentPeriod, view, 1);
  }

  return periods;
}

/**
 * Calculate daily graph data for custom date ranges
 */
function calculateCustomRangeGraphData(
  orderData: OrderHistory[],
  startDate: Date,
  endDate: Date,
  showExVat: boolean
): GraphDataPoint[] {
  const dailyMap = new Map<string, number>();

  // Group orders by day
  orderData.forEach(order => {
    const orderDate = normalizeDate(new Date(order.orderDate));

    // Only include data within the selected range
    if (orderDate >= normalizeDate(startDate) && orderDate <= normalizeDate(endDate)) {
      const dayKey = orderDate.toISOString();
      dailyMap.set(dayKey, (dailyMap.get(dayKey) || 0) + getOrderTotal(order, showExVat));
    }
  });

  // Don't go into the future
  const today = normalizeDate(new Date());
  const effectiveEndDate = endDate > today ? today : endDate;

  // Create array of all days in range (including days with no data)
  const allDays: GraphDataPoint[] = [];
  const currentDay = new Date(startDate);
  currentDay.setHours(0, 0, 0, 0);

  while (currentDay <= effectiveEndDate) {
    const dayKey = currentDay.toISOString();
    allDays.push({
      label: formatPeriodLabel(new Date(currentDay), 'day'),
      value: dailyMap.get(dayKey) || 0,
      periodStart: new Date(currentDay)
    });
    currentDay.setDate(currentDay.getDate() + 1);
  }

  return allDays;
}

/**
 * Format period label for graph display
 */
function formatPeriodLabel(date: Date, view: 'day' | 'week' | 'month' | 'quarter'): string {
  if (view === 'day') {
    // Short format: "Jan 15"
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else if (view === 'week') {
    // Week format: "W2" (week number)
    const yearStart = new Date(date.getFullYear(), 0, 1);
    const weekStart = startOfPeriod(date, 'week');
    const diff = weekStart.getTime() - yearStart.getTime();
    const weekNumber = Math.floor(diff / (1000 * 60 * 60 * 24 * 7)) + 1;
    return `W${weekNumber}`;
  } else if (view === 'month') {
    // Month format: "Jan", "Feb", etc.
    return date.toLocaleDateString('en-US', { month: 'short' });
  } else if (view === 'quarter') {
    // Quarter format: "Q1", "Q2", etc.
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    const yearShort = date.getFullYear() % 100;
    return `Q${quarter} ${yearShort}`;
  }
  return '';
}
